import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { TrendingUp, TrendingDown, Minus } from 'lucide-react'
import {
    DataTableCustomized,
    createColumns,
} from '@/components/ui/data-table-customized'

export interface EmployeeContactData {
    sales_manager_code: number
    employee_name: string
    new_customers_count: number
}

interface EmployeeContactsTableProps {
    /** Current period data aggregated by employee */
    data: EmployeeContactData[]
    /** Previous period data keyed by sales_manager_code */
    previousData?: Record<number, number>
    /** Total contacts for current period */
    totalContacts?: number
    /** Total contacts for previous period */
    previousTotalContacts?: number
    /** Max height for scrollable table */
    maxHeight?: string
    /** Show title header */
    showTitle?: boolean
    /** Custom className */
    className?: string
}

const EmployeeContactsTable: React.FC<EmployeeContactsTableProps> = ({
    data,
    previousData = {},
    totalContacts,
    previousTotalContacts,
    maxHeight = '160px',
    showTitle = true,
    className,
}) => {
    const { t } = useTranslation()

    // Calculate totals if not provided
    const calculatedTotalContacts = useMemo(() => {
        if (totalContacts !== undefined) return totalContacts
        return data.reduce((sum, emp) => sum + emp.new_customers_count, 0)
    }, [data, totalContacts])

    const calculatedPreviousTotal = useMemo(() => {
        if (previousTotalContacts !== undefined) return previousTotalContacts
        return Object.values(previousData).reduce(
            (sum, count) => sum + count,
            0
        )
    }, [previousData, previousTotalContacts])

    // Calculate growth rate
    const calculateGrowth = (current: number, previous: number) => {
        if (previous === 0) return null
        return ((current - previous) / previous) * 100
    }

    const totalGrowthRate = calculateGrowth(
        calculatedTotalContacts,
        calculatedPreviousTotal
    )

    const renderGrowthIndicator = (rate: number | null) => {
        if (rate === null) return null

        if (rate > 0) {
            return (
                <div className="flex items-center justify-end gap-1 text-green-600">
                    <TrendingUp size={16} />
                    <span className="text-sm font-medium">
                        +{rate.toFixed(1)}%
                    </span>
                </div>
            )
        } else if (rate < 0) {
            return (
                <div className="flex items-center justify-end gap-1 text-red-600">
                    <TrendingDown size={16} />
                    <span className="text-sm font-medium">
                        {rate.toFixed(1)}%
                    </span>
                </div>
            )
        }
        return (
            <div className="flex items-center justify-end gap-1 text-gray-500">
                <Minus size={16} />
                <span className="text-sm font-medium">0%</span>
            </div>
        )
    }

    const columns = useMemo(
        () =>
            createColumns<EmployeeContactData>([
                {
                    key: 'employee_name',
                    title: t('employee'),
                    headerClassName: 'text-left uppercase',
                    render: (_, emp) => (
                        <div>
                            {emp.employee_name}
                            <span className="text-xs text-gray-400 ml-1">
                                #{emp.sales_manager_code}
                            </span>
                        </div>
                    ),
                },
                {
                    key: 'new_customers_count',
                    title: t('new_contacts'),
                    headerClassName: 'text-right uppercase',
                    cellClassName: 'text-right text-blue-600 font-medium',
                    width: 120,
                    render: (value) => value.toLocaleString(),
                },
                {
                    key: 'previous' as any,
                    title: t('previous'),
                    headerClassName: 'text-right uppercase',
                    cellClassName: 'text-right text-gray-500',
                    width: 100,
                    render: (_, emp) =>
                        (
                            previousData[emp.sales_manager_code] || 0
                        ).toLocaleString(),
                },
                {
                    key: 'growth' as any,
                    title: t('growth'),
                    headerClassName: 'text-right uppercase',
                    cellClassName: 'text-right',
                    width: 100,
                    render: (_, emp) => {
                        const prevCount =
                            previousData[emp.sales_manager_code] || 0
                        const growth = calculateGrowth(
                            emp.new_customers_count,
                            prevCount
                        )
                        return renderGrowthIndicator(growth)
                    },
                },
            ]),
        [t, previousData]
    )

    if (data.length === 0) return null

    return (
        <div className={className}>
            {showTitle && (
                <div className="text-sm font-medium text-gray-700 mb-2 text-primary capitalize">
                    {t('by_employee')}
                </div>
            )}
            <DataTableCustomized
                columns={columns}
                rows={data}
                maxHeight={maxHeight}
                pinnedHeader
                rowKey={(row) => String(row.sales_manager_code)}
                footerRow={{
                    cells: {
                        employee_name: (
                            <span className="capitalize">{t('total')}</span>
                        ),
                        new_customers_count: (
                            <span className="text-blue-600">
                                {calculatedTotalContacts.toLocaleString()}
                            </span>
                        ),
                        previous: (
                            <span className="text-gray-500">
                                {calculatedPreviousTotal.toLocaleString()}
                            </span>
                        ),
                        growth: renderGrowthIndicator(totalGrowthRate),
                    },
                }}
            />
        </div>
    )
}

export default EmployeeContactsTable
